/**
 * Tâche 1161 (Eric 2026-06-17) — Majuscules en masse, avec aperçu.
 *
 * Le dictionnaire a hérité de centaines de titres en Title Case (« Actifs Sous
 * Gestion ») produits par l'ancienne normalisation. Ici : « avant → après »,
 * **une case à cocher par ligne** (choix d'Eric : on décoche ce qu'on refuse),
 * puis on applique. Rien n'est écrit avant ce clic.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { TextAa, X } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

export const BulkCaseFixModal = ({ open, onClose, onApplied }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [refused, setRefused] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/jargon/case-preview`, {
        withCredentials: true,
      });
      setData(res.data);
      setRefused(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aperçu impossible");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  if (!open) return null;

  const items = data?.items || [];
  const kept = items.filter((i) => !refused.has(i.id));

  const toggle = (id) =>
    setRefused((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const apply = async () => {
    setApplying(true);
    try {
      const res = await axios.post(
        `${API}/api/jargon/case-apply`,
        { items: kept.map((i) => ({ id: i.id, proposed: i.proposed })) },
        { withCredentials: true }
      );
      // Tâche 1161 (2e passage, 2026-06-17) — Eric ne voyait pas que les
      // corrections partaient : la modale se fermait et un toast passait.
      // Le résultat reste maintenant affiché, terme par terme, et c'est
      // l'utilisateur qui ferme.
      setResult(res.data);
      if (onApplied) onApplied();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Application impossible");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        data-testid="bulk-case-modal"
      >
        <div className="flex items-start justify-between px-5 py-4 border-b border-zinc-200">
          <div>
            <h2 className="text-lg font-semibold text-zinc-900 flex items-center gap-2">
              <TextAa size={20} />
              Corriger les majuscules du dictionnaire
            </h2>
            <p className="text-xs text-zinc-500 mt-1 max-w-xl">
              En français, la majuscule à l'intérieur d'un terme est réservée
              aux noms propres et aux acronymes. Décochez ce que vous voulez
              garder tel quel : rien n'est enregistré avant « Appliquer ».
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-zinc-100 rounded"
            data-testid="bulk-case-close"
          >
            <X size={18} />
          </button>
        </div>

        {result && (
          <div
            className="mx-5 mt-3 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2"
            data-testid="bulk-case-result"
          >
            <p className="text-sm font-semibold text-emerald-900">
              {result.applied_count} terme(s) corrigé(s)
              {result.skipped_count ? ` · ${result.skipped_count} ignoré(s)` : ""}
            </p>
            <ul className="mt-1 max-h-40 overflow-y-auto text-xs text-emerald-900">
              {(result.applied || []).map((a) => (
                <li key={a.id} data-testid={`bulk-case-applied-${a.id}`}>
                  « {a.before} » → <strong>« {a.after} »</strong>
                </li>
              ))}
              {(result.skipped || []).map((sk) => (
                <li key={`s-${sk.id}`} className="text-amber-800">
                  {sk.id} — ignoré : {sk.reason}
                </li>
              ))}
            </ul>
            {result.applied_count === 0 && (
              <p className="mt-1 text-xs text-amber-800">
                Aucune correction enregistrée. Si vous attendiez un changement,
                signalez-le : ce n'est pas normal.
              </p>
            )}
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading && (
            <p className="text-sm text-zinc-500" data-testid="bulk-case-loading">
              Analyse du dictionnaire…
            </p>
          )}
          {!loading && !items.length && (
            <p className="text-sm text-zinc-600" data-testid="bulk-case-empty">
              Aucune majuscule à corriger : tout est déjà conforme.
            </p>
          )}
          {!loading && items.length > 0 && (
            <ul className="divide-y divide-zinc-100" data-testid="bulk-case-list">
              {items.map((i) => (
                <li
                  key={i.id}
                  className="py-2 flex items-start gap-3"
                  data-testid={`bulk-case-row-${i.id}`}
                >
                  <input
                    type="checkbox"
                    checked={!refused.has(i.id)}
                    onChange={() => toggle(i.id)}
                    className="mt-1 cursor-pointer"
                    data-testid={`bulk-case-checkbox-${i.id}`}
                  />
                  <div className="text-sm min-w-0">
                    <span className="text-zinc-500 line-through break-words">
                      {i.term}
                    </span>
                    <span className="mx-2 text-zinc-400">→</span>
                    <span className="font-medium text-zinc-900 break-words">
                      {i.proposed}
                    </span>
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-zinc-400">
                      {i.source === "ia" ? "IA" : "règle"}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {data?.truncated && (
            <p
              className="mt-3 text-xs text-amber-700"
              data-testid="bulk-case-truncated"
            >
              Seuls les {data.max_per_pass} premiers termes concernés sont
              proposés. Relancez après application pour voir la suite.
            </p>
          )}
        </div>

        <div className="px-5 py-3 border-t border-zinc-200 flex items-center justify-between">
          <span className="text-xs text-zinc-500" data-testid="bulk-case-count">
            {kept.length} sélectionné(s) sur {items.length} · {data?.scanned || 0}{" "}
            termes analysés
          </span>
          {result ? (
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-zinc-900 text-white text-sm hover:bg-zinc-800"
              data-testid="bulk-case-done"
            >
              Terminé
            </button>
          ) : (
            <button
              onClick={apply}
              disabled={applying || !kept.length}
              className="px-4 py-2 rounded-lg bg-zinc-900 text-white text-sm hover:bg-zinc-800 disabled:opacity-40"
              data-testid="bulk-case-apply"
            >
              {applying ? "Application…" : `Appliquer les ${kept.length} corrections`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkCaseFixModal;
